interface LearningStats {
  streak: number
  totalCompleted: number
  totalHours: number
  completedPaths: number
}

interface LearningStatsHeaderProps {
  stats: LearningStats | null
  isLoading: boolean
}

export default function LearningStatsHeader({ stats, isLoading }: LearningStatsHeaderProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 animate-pulse">
            <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-16 mb-2"></div>
            <div className="h-6 bg-gray-100 dark:bg-gray-700 rounded w-12"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!stats) return null

  const statCards = [
    {
      label: 'Day Streak',
      value: stats.streak,
      suffix: stats.streak === 1 ? 'day' : 'days',
      bgLight: 'bg-gradient-to-br from-orange-50 to-red-50',
      bgDark: 'dark:bg-gradient-to-br dark:from-rose-900/40 dark:to-red-900/40'
    },
    {
      label: 'Items Completed',
      value: stats.totalCompleted,
      suffix: stats.totalCompleted === 1 ? 'item' : 'items',
      bgLight: 'bg-gradient-to-br from-emerald-50 to-teal-50',
      bgDark: 'dark:bg-gradient-to-br dark:from-emerald-900/40 dark:to-teal-900/40'
    },
    {
      label: 'Hours Learned',
      value: Math.round(stats.totalHours * 10) / 10,
      suffix: stats.totalHours === 1 ? 'hour' : 'hours',
      bgLight: 'bg-gradient-to-br from-blue-50 to-cyan-50',
      bgDark: 'dark:bg-gradient-to-br dark:from-blue-900/40 dark:to-cyan-900/40'
    },
    {
      label: 'Paths Completed',
      value: stats.completedPaths,
      suffix: stats.completedPaths === 1 ? 'path' : 'paths',
      bgLight: 'bg-gradient-to-br from-violet-50 to-purple-50',
      bgDark: 'dark:bg-gradient-to-br dark:from-violet-900/40 dark:to-purple-900/40'
    }
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {statCards.map((stat) => (
          <div
            key={stat.label}
            className={`${stat.bgLight} ${stat.bgDark} rounded-xl px-4 py-3 border border-gray-100 dark:border-gray-700 memtrail-stat`}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {stat.label}
            </p>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                {stat.value}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500">
                {stat.suffix}
              </span>
            </div>
          </div>
        ))}
    </div>
  )
}
